// Angular Imports
import { Router, NavigationEnd } from '@angular/router'
import { Location } from '@angular/common'
import { Component, OnDestroy, OnInit, inject } from '@angular/core'
// Shared Imports
import { RegisterSvgAsIconService, GoogleAnalyticsService } from '@shared/services'
import {
	NgcCookieConsentService,
	NgcInitializationErrorEvent,
	NgcInitializingEvent,
	NgcNoCookieLawEvent,
	NgcStatusChangeEvent,
} from 'ngx-cookieconsent'
import { Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
	// INFO: this DI is here to make all svg icons declared within the register-svg-as-icon service globally available
	readonly #registerSvgAsIcon = inject(RegisterSvgAsIconService)
	readonly #router = inject(Router)
	readonly #location = inject(Location)
	readonly #googleAnalytics = inject(GoogleAnalyticsService)
	private popupOpenSubscription!: Subscription
	private popupCloseSubscription!: Subscription
	private initializingSubscription!: Subscription
	private initializedSubscription!: Subscription
	private initializationErrorSubscription!: Subscription
	private statusChangeSubscription!: Subscription
	private revokeChoiceSubscription!: Subscription
	private noCookieLawSubscription!: Subscription
	private routerSubscription!: Subscription

	constructor(private readonly cookieService: NgcCookieConsentService) {}

	ngOnInit() {
		// Removemos los tokens de la url luego de hacer login
		const searchParams = new URLSearchParams(this.#location.path(false).split('?')[1])
		searchParams.delete('access-token')
		searchParams.delete('refresh-token')

		let urlWithoutTokensParams: string = ''
		searchParams.toString()
			? (urlWithoutTokensParams = `${
					this.#location.path().split('?')[0]
				}?${searchParams.toString()}`)
			: (urlWithoutTokensParams = `${this.#location.path().split('?')[0]}`)

		this.#router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			this.#router.navigateByUrl(urlWithoutTokensParams)
		})

		// Track page views automatically on route changes
		this.routerSubscription = this.#router.events
			.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				this.#googleAnalytics.trackPageView(document.title, event.urlAfterRedirects)
			})

		this.popupOpenSubscription = this.cookieService.popupOpen$.subscribe(() => {
			// you can use this.cookieService.getConfig() to do stuff...
		})

		this.popupCloseSubscription = this.cookieService.popupClose$.subscribe(() => {
			// you can use this.cookieService.getConfig() to do stuff...
		})

		this.initializingSubscription = this.cookieService.initializing$.subscribe(
			(event: NgcInitializingEvent) => {
				// the cookieconsent is initilializing... Not yet safe to call methods like `NgcCookieConsentService.hasAnswered()`
			}
		)

		this.initializedSubscription = this.cookieService.initialized$.subscribe(() => {
			// the cookieconsent has been successfully initialized.
			// It's now safe to use methods on NgcCookieConsentService that require it, like `hasAnswered()` for eg...
		})

		this.initializationErrorSubscription = this.cookieService.initializationError$.subscribe(
			(event: NgcInitializationErrorEvent) => {
				// the cookieconsent has failed to initialize...
			}
		)

		this.statusChangeSubscription = this.cookieService.statusChange$.subscribe(
			(event: NgcStatusChangeEvent) => {
				// you can use this.cookieService.getConfig() to do stuff...
			}
		)

		this.revokeChoiceSubscription = this.cookieService.revokeChoice$.subscribe(() => {
			// you can use this.cookieService.getConfig() to do stuff...
		})

		this.noCookieLawSubscription = this.cookieService.noCookieLaw$.subscribe(
			(event: NgcNoCookieLawEvent) => {
				// you can use this.cookieService.getConfig() to do stuff...
			}
		)
	}

	ngOnDestroy() {
		// unsubscribe to cookieconsent observables to prevent memory leaks
		this.popupOpenSubscription.unsubscribe()
		this.popupCloseSubscription.unsubscribe()
		this.initializingSubscription.unsubscribe()
		this.initializedSubscription.unsubscribe()
		this.initializationErrorSubscription.unsubscribe()
		this.statusChangeSubscription.unsubscribe()
		this.revokeChoiceSubscription.unsubscribe()
		this.noCookieLawSubscription.unsubscribe()
		this.routerSubscription.unsubscribe()
	}
}
